import React, { useEffect, useMemo, useRef, useState } from "react";
import BrowserVoiceWidget from "../components/BrowserVoiceWidget";
import {
  getBrowserVoiceWsUrl,
  getBrowserVoiceRealtimeWsUrl,
  getBrowserVoicePttWsUrl,
  getBrowserVoiceKokoroWsUrl,
  getBrowserVoiceKokoroVadWsUrl,
  getBrowserVoiceKokoroWsTtsUrl,
  getBrowserVoiceV2WsUrl,
  getRealtimeTokenUrl,
} from "../lib/apiConfig";
//Test
const LEGACY_WS_URL = getBrowserVoiceWsUrl();
const REALTIME_WS_URL = getBrowserVoiceRealtimeWsUrl();
const PTT_WS_URL = getBrowserVoicePttWsUrl();
const KOKORO_WS_URL = getBrowserVoiceKokoroWsUrl();
const KOKORO_VAD_WS_URL = getBrowserVoiceKokoroVadWsUrl();
const KOKORO_VAD_WS_TTS_URL = getBrowserVoiceKokoroWsTtsUrl();
const V2_WS_URL = getBrowserVoiceV2WsUrl();
const REALTIME_TOKEN_URL = getRealtimeTokenUrl();
const HAS_REALTIME_ENDPOINT = Boolean(REALTIME_WS_URL);
const HAS_PTT_ENDPOINT = Boolean(PTT_WS_URL);
const HAS_KOKORO_ENDPOINT = Boolean(KOKORO_WS_URL);
const HAS_KOKORO_VAD_ENDPOINT = Boolean(KOKORO_VAD_WS_URL);
const HAS_KOKORO_VAD_WS_TTS_ENDPOINT = Boolean(KOKORO_VAD_WS_TTS_URL);
const HAS_V2_ENDPOINT = Boolean(V2_WS_URL);
const DEFAULT_BACKEND_MODE =
  HAS_REALTIME_ENDPOINT &&
    (process.env.REACT_APP_BROWSER_DEFAULT_BACKEND || "").toLowerCase() ===
    "realtime"
    ? "realtime"
    : HAS_PTT_ENDPOINT &&
      (process.env.REACT_APP_BROWSER_DEFAULT_BACKEND || "").toLowerCase() ===
      "ptt"
      ? "ptt"
      : HAS_KOKORO_ENDPOINT &&
        (process.env.REACT_APP_BROWSER_DEFAULT_BACKEND || "").toLowerCase() ===
        "kokoro"
        ? "kokoro"
        : HAS_KOKORO_VAD_ENDPOINT &&
          (process.env.REACT_APP_BROWSER_DEFAULT_BACKEND || "").toLowerCase() ===
          "kokoro-vad"
          ? "kokoro-vad"
          : "legacy";

// Kokoro TTS voices (for Kokoro mode)
const KOKORO_VOICES = [
  { id: "af_heart", name: "Heart (Female)", language: "en" },
  { id: "af_bella", name: "Bella (Female)", language: "en" },
  { id: "af_nicole", name: "Nicole (Female)", language: "en" },
  { id: "af_sarah", name: "Sarah (Female)", language: "en" },
  { id: "af_sky", name: "Sky (Female)", language: "en" },
  { id: "am_adam", name: "Adam (Male)", language: "en" },
  { id: "am_michael", name: "Michael (Male)", language: "en" },
  { id: "bf_emma", name: "Emma (British Female)", language: "en" },
  { id: "bf_isabella", name: "Isabella (British Female)", language: "en" },
  { id: "bm_george", name: "George (British Male)", language: "en" },
  { id: "bm_lewis", name: "Lewis (British Male)", language: "en" },
];

const FALLBACK_VOICES = [
  // Original voices
  { id: "JBFqnCBsd6RMkjVDRZzb", name: "Rachel (Default)", language: "en" },
  { id: "XrExE9yKIg1WjnnlVkGX", name: "Maya", language: "en" },
  { id: "21m00Tcm4TlvDq8ikWAM", name: "Rachel (Alt)", language: "en" },
  { id: "AZnzlk1XvdvUeBnXmlld", name: "Domi", language: "en" },
  { id: "EXAVITQu4vr4xnSDxMaL", name: "Bella", language: "en" },
  { id: "ErXwobaYiN019PkySvjV", name: "Antoni", language: "en" },
  { id: "TxGEqnHWrfWFTfGW9XjX", name: "Josh", language: "en" },
  { id: "pNInz6obpgDQGcFmaJgB", name: "Adam", language: "en" },

  // Emotional/Style voices (from Outbound)
  { id: "Qggl4b0xRMiqOwhPtVWT", name: "Empathetic", language: "en" },
  { id: "tnSpp4vdxKPjI9w0GnoV", name: "Energetic", language: "en" },
  { id: "cgSgspJ2msm6clMCkdW9", name: "Playful", language: "en" },
  { id: "zmcVlqmyk3Jpn5AVYcAL", name: "Sweet", language: "en" },

  // Female voices
  { id: "EXAVITQu4vr4xnSDxMaL", name: "Sarah", language: "en" },
  { id: "MF3mGyEYCl7XYWbV9V6O", name: "Elli", language: "en" },
  { id: "XB0fDUnXU5powFXDhCwa", name: "Charlotte", language: "en" },
  { id: "pMsXgVXv3BLzUgSXRplE", name: "Serena", language: "en" },
  { id: "oWAxZDx7w5VEj9dCyTzz", name: "Grace", language: "en" },
  { id: "2EiwWnXFnvU5JabPnv8n", name: "Clyde", language: "en" },
  { id: "SOYHLrjzK2X1ezoPC6cr", name: "Emily", language: "en" },
  { id: "IKne3meq5aSn9XLyUdCD", name: "Freya", language: "en" },
  { id: "jsCqWAovK2LkecY7zXl4", name: "Jessica", language: "en" },
  { id: "LcfcDJNUP1GQjkzn1xUU", name: "Lily", language: "en" },
  { id: "ThT5KcBeYPX3keUQqHPh", name: "Nicole", language: "en" },
  { id: "jBpfuIE2acCO8z3wKNLl", name: "Olivia", language: "en" },
  { id: "XrExE9yKIg1WjnnlVkGX", name: "Matilda", language: "en" },
  { id: "piTKgcLEGmPE4e6mEKli", name: "Dorothy", language: "en" },
  { id: "j45mXgB0BR0mIJbdyK09", name: "Daisy", language: "en" },

  // Male voices
  { id: "VR6AewLTigWG4xSOukaG", name: "Arnold", language: "en" },
  { id: "pqHfZKP75CvOlQylNhV4", name: "Bill", language: "en" },
  { id: "blhsbJ47xCbCpTpQwGyK", name: "Miranda (Warm/Southern)", language: "en" },
  { id: "N2lVS1w4EtoT3dr4eOWO", name: "Callum", language: "en" },
  { id: "IKne3meq5aSn9XLyUdCD", name: "Charlie", language: "en" },
  { id: "onwK4e9ZLuTAKqWW03F9", name: "Daniel", language: "en" },
  { id: "Zlb1dXrM653N07WRdFW3", name: "Drew", language: "en" },
  { id: "yoZ06aMxZJJ28mfd3POQ", name: "Ethan", language: "en" },
  { id: "iP95p4xoKVk53GoZ742B", name: "Fin", language: "en" },
  { id: "g5CIjZEefAph4nQFvHAz", name: "George", language: "en" },
  { id: "CwhRBWXzGAHq8TQ4Fs17", name: "Harry", language: "en" },
  { id: "Yko7PKHZNXotIFUBG7I9", name: "James", language: "en" },
  { id: "TX3LPaxmHKxFdv7VOQHJ", name: "Joseph", language: "en" },
  { id: "bVMeCyTHy58xNoL34h3p", name: "Jeremy", language: "en" },
  { id: "ZQe5CZNOzWyzPSCn5a3c", name: "Liam", language: "en" },
  { id: "CYw3kZ02Hs0563khs1Fj", name: "Marcus", language: "en" },
  { id: "flq6f7yk4E4fJM5XTYuZ", name: "Michael", language: "en" },
  { id: "ODq5zmih8GrVes37Dizd", name: "Patrick", language: "en" },
  { id: "GBv7mTt0atIp3Br8iCZE", name: "Sam", language: "en" },
  { id: "TxGEqnHWrfWFTfGW9XjX", name: "Thomas", language: "en" },
  { id: "bIHbv24MWmeRgasZH58o", name: "Will", language: "en" },

  // Additional diverse voices
  { id: "cgSgspJ2msm6clMCkdW9", name: "Giovanni", language: "en" },
  { id: "FGY2WhTYpPnrIDTdsKH5", name: "Glinda", language: "en" },
  { id: "t0jbNlBVZ17f02VDIeMI", name: "Gigi", language: "en" },
  { id: "pFZP5JQG7iQjIQuC4Bku", name: "Laura", language: "en" },
  { id: "D38z5RcWu1voky8WS1ja", name: "Aria", language: "en" },
  { id: "EmuDoxBKqQik7OFqGEMt", name: "River", language: "en" },
  { id: "cjVigY5qzO86Huf0OWal", name: "Eric", language: "en" },
  { id: "zrHiDhphv9ZnVXBqCLjz", name: "Mimi", language: "en" },

  // Extended voice library
  { id: "pFZP5JQG7iQjIQuC4Bku", name: "Natalie", language: "en" },
  { id: "ODq5zmih8GrVes37Dizd", name: "Roger", language: "en" },
  { id: "IKne3meq5aSn9XLyUdCD", name: "Amelia", language: "en" },
  { id: "5Q0t7uMcjvnagumLfvZi", name: "Paul", language: "en" },
  { id: "ZF6FPAbjXT4488VcRRnw", name: "Danielle", language: "en" },
  { id: "2EiwWnXFnvU5JabPnv8n", name: "Dave", language: "en" },
  { id: "D38z5RcWu1voky8WS1ja", name: "Fin (Alt)", language: "en" },
  { id: "pNInz6obpgDQGcFmaJgB", name: "Giovanni (Alt)", language: "en" },
  { id: "MF3mGyEYCl7XYWbV9V6O", name: "Alice", language: "en" },
  { id: "TxGEqnHWrfWFTfGW9XjX", name: "Chris", language: "en" },
  { id: "EXAVITQu4vr4xnSDxMaL", name: "Serena (Alt)", language: "en" },
  { id: "ThT5KcBeYPX3keUQqHPh", name: "Matilda (Alt)", language: "en" },
  { id: "flq6f7yk4E4fJM5XTYuZ", name: "Gigi (Alt)", language: "en" },
  { id: "ODq5zmih8GrVes37Dizd", name: "Callum (Alt)", language: "en" },
  { id: "g5CIjZEefAph4nQFvHAz", name: "Lily (Alt)", language: "en" },
  { id: "piTKgcLEGmPE4e6mEKli", name: "Bill (Alt)", language: "en" },
  { id: "VR6AewLTigWG4xSOukaG", name: "Jessie", language: "en" },
  { id: "pqHfZKP75CvOlQylNhV4", name: "Mark", language: "en" },
  { id: "N2lVS1w4EtoT3dr4eOWO", name: "Barbara", language: "en" },
  { id: "onwK4e9ZLuTAKqWW03F9", name: "Victoria", language: "en" },
  { id: "Zlb1dXrM653N07WRdFW3", name: "Ryan", language: "en" },
  { id: "yoZ06aMxZJJ28mfd3POQ", name: "Eleanor", language: "en" },
  { id: "iP95p4xoKVk53GoZ742B", name: "Liam (Alt)", language: "en" },
  { id: "CwhRBWXzGAHq8TQ4Fs17", name: "Bernard", language: "en" },
  { id: "Yko7PKHZNXotIFUBG7I9", name: "Sophia", language: "en" },
  { id: "TX3LPaxmHKxFdv7VOQHJ", name: "Kevin", language: "en" },
  { id: "bVMeCyTHy58xNoL34h3p", name: "Madison", language: "en" },
  { id: "ZQe5CZNOzWyzPSCn5a3c", name: "Nathan", language: "en" },
  { id: "CYw3kZ02Hs0563khs1Fj", name: "Isabella", language: "en" },
  { id: "GBv7mTt0atIp3Br8iCZE", name: "Alexander", language: "en" },
  { id: "bIHbv24MWmeRgasZH58o", name: "Emma", language: "en" },
  { id: "jBpfuIE2acCO8z3wKNLl", name: "Oliver", language: "en" },
  { id: "jsCqWAovK2LkecY7zXl4", name: "Mia", language: "en" },
  { id: "LcfcDJNUP1GQjkzn1xUU", name: "Lucas", language: "en" },
];

export default function BrowserVoice() {
  const [backendMode, setBackendMode] = useState(DEFAULT_BACKEND_MODE);

  // Get voices based on backend mode
  const voices = useMemo(() => {
    if (backendMode === "kokoro" || backendMode === "kokoro-vad" || backendMode === "kokoro-vad-ws") {
      return KOKORO_VOICES;
    }
    return FALLBACK_VOICES;
  }, [backendMode]);

  // Configuration state
  const [selectedVoice, setSelectedVoice] = useState(() => {
    const sweet = FALLBACK_VOICES.find(v => v.name === "Sweet");
    return sweet || FALLBACK_VOICES[0];
  });

  // Update selected voice when backend mode changes
  useEffect(() => {
    if (backendMode === "kokoro" || backendMode === "kokoro-vad" || backendMode === "kokoro-vad-ws") {
      const heart = KOKORO_VOICES.find(v => v.id === "af_heart");
      setSelectedVoice(heart || KOKORO_VOICES[0]);
    } else {
      const sweet = FALLBACK_VOICES.find(v => v.name === "Sweet");
      setSelectedVoice(sweet || FALLBACK_VOICES[0]);
    }
  }, [backendMode]);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [agentName, setAgentName] = useState("Maya");
  const [firstMessage, setFirstMessage] = useState("Hi! I'm Maya, your AI assistant. How can I help you today?");
  const [firstMessageEdited, setFirstMessageEdited] = useState(false);
  const [scripts, setScripts] = useState([]);
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } })();
  const [selectedScriptId, setSelectedScriptId] = useState("");
  const [selectedScriptData, setSelectedScriptData] = useState(null);
  const activeWsUrl = useMemo(() => {
    if (backendMode === "realtime" && HAS_REALTIME_ENDPOINT) {
      return REALTIME_WS_URL;
    }
    if (backendMode === "ptt" && HAS_PTT_ENDPOINT) {
      return PTT_WS_URL;
    }
    if (backendMode === "kokoro" && HAS_KOKORO_ENDPOINT) {
      return KOKORO_WS_URL;
    }
    if (backendMode === "kokoro-vad" && HAS_KOKORO_VAD_ENDPOINT) {
      return KOKORO_VAD_WS_URL;
    }
    if (backendMode === "kokoro-vad-ws" && HAS_KOKORO_VAD_WS_TTS_ENDPOINT) {
      return KOKORO_VAD_WS_TTS_URL;
    }
    if (backendMode === "v2" && HAS_V2_ENDPOINT) {
      return V2_WS_URL;
    }
    return LEGACY_WS_URL;
  }, [backendMode]);
  const backendModeOptions = useMemo(
    () => [
      {
        id: "legacy",
        label: "Legacy Stack",
        description: "Deepgram STT + GPT-4o-mini + ElevenLabs TTS (Open mic with VAD)",
        disabled: false,
      },
      {
        id: "v2",
        label: "V2 Stack ⚡",
        description: HAS_V2_ENDPOINT
          ? "Optimized VAD + faster endpointing (Open mic, lower latency)"
          : "Set REACT_APP_BROWSER_V2_WS to enable",
        disabled: !HAS_V2_ENDPOINT,
      },
      {
        id: "ptt",
        label: "Push-to-Talk",
        description: HAS_PTT_ENDPOINT
          ? "Hold button to speak, release to send (No VAD latency)"
          : "Set REACT_APP_BROWSER_PTT_WS to enable",
        disabled: !HAS_PTT_ENDPOINT,
      },
      {
        id: "kokoro",
        label: "Kokoro PTT",
        description: HAS_KOKORO_ENDPOINT
          ? "Deepgram STT + GPT-4o-mini + Kokoro TTS (Push-to-Talk)"
          : "Set REACT_APP_BROWSER_KOKORO_WS to enable",
        disabled: !HAS_KOKORO_ENDPOINT,
      },
      {
        id: "kokoro-vad",
        label: "Kokoro VAD 🎶",
        description: HAS_KOKORO_VAD_ENDPOINT
          ? "Deepgram STT + GPT-4o-mini + Kokoro TTS HTTP (Open mic with VAD)"
          : "Set REACT_APP_BROWSER_KOKORO_VAD_WS to enable",
        disabled: !HAS_KOKORO_VAD_ENDPOINT,
      },
      {
        id: "kokoro-vad-ws",
        label: "Kokoro VAD WS 🎤",
        description: HAS_KOKORO_VAD_WS_TTS_ENDPOINT
          ? "Deepgram STT + GPT-4o-mini + Kokoro TTS WebSocket (Open mic with VAD)"
          : "Set REACT_APP_BROWSER_KOKORO_WS_TTS to enable",
        disabled: !HAS_KOKORO_VAD_WS_TTS_ENDPOINT,
      },
      {
        id: "realtime",
        label: "Realtime API",
        description: HAS_REALTIME_ENDPOINT
          ? "OpenAI Realtime API (experimental)"
          : "Set REACT_APP_BROWSER_REALTIME_WS to enable",
        disabled: !HAS_REALTIME_ENDPOINT,
      },
    ],
    []
  );

  // Session state
  const [sessionActive, setSessionActive] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [metrics, setMetrics] = useState({ detection: 0, kb: 0, gpt: 0, tts: 0, total: 0 });
  const [totalSessions, setTotalSessions] = useState(0);
  const [avgLatency, setAvgLatency] = useState(0);
  const [latencyLog, setLatencyLog] = useState([]);
  const latestUserRef = useRef("");
  const latestAssistantRef = useRef("");

  const backendModeLabel =
    backendMode === "realtime"
      ? "OpenAI Realtime API"
      : "Deepgram + GPT + ElevenLabs";
  const MAX_LATENCY_LOG = 50;
  const countWords = (text = "") => {
    const trimmed = text?.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).length;
  };
  const formatTime = (timestamp) => new Date(timestamp).toLocaleTimeString();
  const latencyStats = useMemo(() => {
    const exchanges = latencyLog.length;
    if (!exchanges) {
      return {
        exchanges: 0,
        avgTotal: 0,
        minTotal: 0,
        maxTotal: 0,
        avgStt: 0,
        avgKb: 0,
        avgGpt: 0,
        avgTts: 0,
      };
    }

    const sumTotals = { total: 0, stt: 0, kb: 0, gpt: 0, tts: 0 };
    const totals = latencyLog.map((entry) => {
      const totalValue = entry.total || entry.stt + entry.kb + entry.gpt + entry.tts;
      sumTotals.total += totalValue;
      sumTotals.stt += entry.stt || 0;
      sumTotals.kb += entry.kb || 0;
      sumTotals.gpt += entry.gpt || 0;
      sumTotals.tts += entry.tts || 0;
      return totalValue;
    });

    return {
      exchanges,
      avgTotal: Math.round(sumTotals.total / exchanges),
      minTotal: Math.min(...totals),
      maxTotal: Math.max(...totals),
      avgStt: Math.round(sumTotals.stt / exchanges),
      avgKb: Math.round(sumTotals.kb / exchanges),
      avgGpt: Math.round(sumTotals.gpt / exchanges),
      avgTts: Math.round(sumTotals.tts / exchanges),
    };
  }, [latencyLog]);

  // Load scripts for selection (shared API used by ScriptManager)
  useEffect(() => {
    const fetchScripts = async () => {
      try {
        const token = localStorage.getItem('token');
        const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
        const res = await fetch(`${apiBase}/api/scripts`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        const data = await res.json();
        if (data && data.scripts) {
          let list = (data.scripts || [])
            .map((s) => ({ ...s, type: s.type || "Text Prompt" }))
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          if (currentUser && Number(currentUser.userlevel) === 100) {
            const myId = currentUser.id || currentUser._id || currentUser.userId;
            list = list.filter(s => String(s.ownerId) === String(myId));
          }
          setScripts(list);
        }
      } catch (err) {
        console.error("❌ Error fetching scripts:", err);
      }
    };
    fetchScripts();
  }, []);

  // Load full script document when a script is selected
  const loadScriptDetail = async (id) => {
    if (!id) {
      setSelectedScriptData(null);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
      const res = await fetch(`${apiBase}/api/script/${id}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = await res.json();
      if (data && data.success) {
        setSelectedScriptData(data.script || null);
      }
    } catch (err) {
      console.error("❌ Error loading script:", err);
      setSelectedScriptData(null);
    }
  };

  // Build a natural first message similar to outbound flow
  const buildFirstMessage = (name, script) => {
    const businessName = (script && (script.businessName || script.scriptName)) || 'our office';
    const aiName = (script && script.aiName) || name || 'Maya'; // Use script's aiName if available, otherwise use name prop, fallback to 'Maya'
    const firstName = script && script.firstName ? script.firstName : null;
    const lastName = script && script.lastName ? script.lastName : null;
    const recipient = firstName ? `${firstName}${lastName ? ' ' + lastName : ''}` : 'you';
    return `Hi there! This is ${aiName}, an AI agent from ${businessName}! Is this ${recipient}`.trim();
  };

  // Keep greeting synced with agent name and selected script until user edits greeting manually
  useEffect(() => {
    if (!firstMessageEdited) {
      setFirstMessage(buildFirstMessage(agentName, selectedScriptData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentName, selectedScriptData]);

  // Handle transcript updates
  const handleTranscript = (role, text) => {
    if (role === "user") {
      latestUserRef.current = text;
    } else if (role === "assistant") {
      latestAssistantRef.current = text;
    }
    setTranscript((prev) => [...prev, { role, text, timestamp: Date.now() }]);
  };

  // Handle metrics updates
  const handleMetrics = (newMetrics) => {
    const totalValue = newMetrics?.total || 0;
    setMetrics(newMetrics);

    // Update average latency
    setAvgLatency((prev) => {
      const count = totalSessions + 1;
      return Math.round((prev * totalSessions + totalValue) / count);
    });

    // Append latency breakdown entry
    setLatencyLog((prev) => {
      const entry = {
        id: prev.length + 1,
        timestamp: Date.now(),
        userText: latestUserRef.current || "",
        assistantText: latestAssistantRef.current || "",
        userChars: (latestUserRef.current || "").length,
        userWords: countWords(latestUserRef.current || ""),
        chars: (latestAssistantRef.current || "").length,
        words: countWords(latestAssistantRef.current || ""),
        stt: newMetrics?.detection ?? newMetrics?.stt ?? 0,
        kb: newMetrics?.kb ?? 0,
        gpt: newMetrics?.gpt ?? 0,
        tts: newMetrics?.tts ?? 0,
        total: totalValue || (newMetrics?.detection ?? 0) + (newMetrics?.kb ?? 0) + (newMetrics?.gpt ?? 0) + (newMetrics?.tts ?? 0)
      };
      const next = [entry, ...prev];
      return next.slice(0, MAX_LATENCY_LOG);
    });
  };

  // Handle status changes
  const handleStatusChange = (status) => {
    if (status === 'listening' || status === 'processing' || status === 'speaking') {
      setSessionActive(true);
    } else if (status === 'disconnected') {
      setSessionActive(false);
      if (transcript.length > 0) {
        setTotalSessions((prev) => prev + 1);
      }
    }
  };

  // Clear transcript
  const clearTranscript = () => {
    setTranscript([]);
  };
  const clearLatencyLog = () => setLatencyLog([]);

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-500 mb-1">Total Sessions</div>
            <div className="text-3xl font-bold text-blue-600">{totalSessions}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-500 mb-1">Avg Latency</div>
            <div className="text-3xl font-bold text-green-600">{avgLatency}ms</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-500 mb-1">Last Latency</div>
            <div className="text-3xl font-bold text-purple-600">{metrics.total || 0}ms</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-500 mb-1">Status</div>
            <div className={`text-2xl font-bold ${sessionActive ? 'text-green-600' : 'text-gray-400'}`}>
              {sessionActive ? '🟢 Active' : '⚪ Idle'}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Configuration Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
              <h2 className="text-xl font-bold text-gray-800 mb-4">⚙️ Settings</h2>

              <div className="space-y-4">
                {/* Agent Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Agent Name
                  </label>
                  <input
                    type="text"
                    value={agentName}
                    onChange={(e) => setAgentName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Maya"
                    disabled={sessionActive}
                  />
                </div>

                {/* First Message */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Greeting (First Message)
                  </label>
                  <input
                    type="text"
                    value={firstMessage}
                    onChange={(e) => { setFirstMessage(e.target.value); setFirstMessageEdited(true); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={`Hi! I'm ${agentName}, your AI assistant. How can I help you today?`}
                    disabled={sessionActive}
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    This greeting is spoken first when the session starts
                  </p>
                </div>
                {/* Script Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Script
                  </label>
                  <select
                    value={selectedScriptId}
                    onChange={async (e) => {
                      const id = e.target.value;
                      setSelectedScriptId(id);
                      setFirstMessageEdited(false); // allow auto-regeneration on new script
                      await loadScriptDetail(id);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={sessionActive}
                  >
                    <option value="">None (no script)</option>
                    {scripts.map((s) => (
                      <option key={s._id} value={s._id}>
                        {s.scriptName}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-xs text-gray-500">
                    Choose a predefined script to guide the assistant
                  </p>
                </div>

                {/* Backend Mode Toggle */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Backend Mode
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {backendModeOptions.map((option) => {
                      const isActive = backendMode === option.id;
                      const isDisabled = sessionActive || option.disabled;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => !isDisabled && setBackendMode(option.id)}
                          disabled={isDisabled}
                          className={`w-full text-left border rounded-lg p-3 transition-colors ${isActive
                            ? "border-blue-500 bg-blue-50 shadow-sm"
                            : "border-gray-200 bg-white"
                            } ${isDisabled && !isActive ? "opacity-60 cursor-not-allowed" : ""}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-800">
                              {option.label}
                            </span>
                            {isActive && (
                              <span className="text-xs font-semibold text-blue-600">
                                Active
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            {option.description}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                  {!HAS_REALTIME_ENDPOINT && (
                    <p className="mt-2 text-xs text-amber-600">
                      Realtime mode requires REACT_APP_BROWSER_REALTIME_WS to be set in
                      your front-end environment.
                    </p>
                  )}
                </div>

                {/* Voice Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Voice ({voices.length} available)
                  </label>
                  <select
                    value={selectedVoice.id}
                    onChange={(e) => {
                      const voice = voices.find(v => v.id === e.target.value);
                      setSelectedVoice(voice);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={sessionActive}
                  >
                    {(backendMode === "kokoro" || backendMode === "kokoro-vad") ? (
                      // Kokoro voices - simple list
                      <>
                        <optgroup label="🎵 Kokoro Voices">
                          {voices.map((voice, idx) => (
                            <option key={`${voice.id}-${idx}`} value={voice.id}>
                              {voice.name} {voice.language && `(${voice.language})`}
                            </option>
                          ))}
                        </optgroup>
                      </>
                    ) : (
                      // ElevenLabs voices - grouped
                      <>
                        <optgroup label="✨ Original Voices">
                          {voices.slice(0, 8).filter(v => v.name !== "Rachel (Default)").map((voice, idx) => (
                            <option key={`${voice.id}-${idx}`} value={voice.id}>
                              {voice.name} {voice.language && `(${voice.language})`}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="💖 Emotional/Style Voices">
                          {voices.slice(8, 12).map((voice, idx) => (
                            <option key={`${voice.id}-${8 + idx}`} value={voice.id}>
                              {voice.name} {voice.language && `(${voice.language})`}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="👩 Female Voices">
                          {voices.slice(12, 26).map((voice, idx) => (
                            <option key={`${voice.id}-${12 + idx}`} value={voice.id}>
                              {voice.name} {voice.language && `(${voice.language})`}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="👨 Male Voices">
                          {voices.slice(26, 47).map((voice, idx) => (
                            <option key={`${voice.id}-${26 + idx}`} value={voice.id}>
                              {voice.name} {voice.language && `(${voice.language})`}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="🎭 Diverse Voices">
                          {voices.slice(47, 55).map((voice, idx) => (
                            <option key={`${voice.id}-${47 + idx}`} value={voice.id}>
                              {voice.name} {voice.language && `(${voice.language})`}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="🌟 Extended Library">
                          {voices.slice(55).map((voice, idx) => (
                            <option key={`${voice.id}-${55 + idx}`} value={voice.id}>
                              {voice.name} {voice.language && `(${voice.language})`}
                            </option>
                          ))}
                        </optgroup>
                      </>
                    )}
                  </select>
                  <p className="mt-2 text-xs text-gray-500">
                    {(backendMode === "kokoro" || backendMode === "kokoro-vad")
                      ? `Choose from ${voices.length} Kokoro TTS voices`
                      : `Choose from ${voices.length} high-quality voices`
                    }
                  </p>
                </div>

                {/* System Prompt */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    System Prompt
                  </label>
                  <textarea
                    value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="4"
                    disabled={sessionActive}
                    placeholder="Example: You are Maya, a helpful, friendly AI voice assistant. Keep responses concise and natural for voice conversation."
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    This defines the AI's personality and behavior
                  </p>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-2">💡 Quick Guide</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Choose your preferred voice</li>
                <li>• Customize the system prompt</li>
                <li>• Click "Start Voice Agent"</li>
                <li>• Allow microphone access</li>
                <li>• Speak naturally!</li>
                <li>• Watch real-time metrics</li>
              </ul>
            </div>
          </div>

          {/* Voice Agent Widget */}
          <div className="lg:col-span-2">
            <BrowserVoiceWidget
              wsUrl={activeWsUrl}
              backendMode={backendMode}
              realtimeTokenUrl={REALTIME_TOKEN_URL}
              scriptId={selectedScriptId || null}
              collectionIds={null}
              voiceId={selectedVoice.id}
              firstMessage={firstMessage}
              agentName={agentName}
              systemPrompt={systemPrompt}
              onTranscript={handleTranscript}
              onMetrics={handleMetrics}
              onStatusChange={handleStatusChange}
              showTranscript={true}
              showMetrics={true}
              showVisualizer={true}
            />

            {/* Connection Info */}
            <div className="mt-6 bg-gray-100 rounded-lg p-4">
              <div className="text-xs text-gray-600 space-y-1">
                <div><span className="font-semibold">Backend Mode:</span> {backendModeLabel}</div>
                <div><span className="font-semibold">WebSocket:</span> {activeWsUrl}</div>
                <div><span className="font-semibold">Voice:</span> {selectedVoice.name}</div>
                <div><span className="font-semibold">Voice ID:</span> {selectedVoice.id}</div>
                <div><span className="font-semibold">Knowledge Base:</span> Configure on phone number</div>
              </div>
            </div>
          </div>
        </div>

        {/* Session History */}
        {transcript.length > 0 && (
          <div className="mt-4 bg-white rounded-lg shadow-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">📝 Full Session History</h2>
              <button
                onClick={clearTranscript}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-semibold"
              >
                Clear History
              </button>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {transcript.map((entry, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg ${entry.role === 'user'
                    ? 'bg-blue-50 border-l-4 border-blue-400'
                    : 'bg-purple-50 border-l-4 border-purple-400'
                    }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs font-semibold uppercase text-gray-600">
                      {entry.role}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(entry.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-800">{entry.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Latency Breakdown */}
        <div className="mt-4 bg-white rounded-lg shadow-lg p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">💬 Conversation Latency Breakdown</h2>
              <p className="text-sm text-gray-500">Detailed input/output metrics for each Deepgram + GPT + ElevenLabs exchange</p>
            </div>
            <div className="flex flex-wrap gap-3 text-sm text-gray-600">
              <div className="px-3 py-2 rounded-lg bg-gray-100">
                <span className="font-semibold text-gray-900">{latencyStats.exchanges}</span> exchanges
              </div>
              <div className="px-3 py-2 rounded-lg bg-gray-100">
                Avg: <span className="font-semibold text-gray-900">{latencyStats.avgTotal || 0}ms</span>
              </div>
              <div className="px-3 py-2 rounded-lg bg-green-100 text-green-700">
                Min: <span className="font-semibold">{latencyStats.minTotal || 0}ms</span>
              </div>
              <div className="px-3 py-2 rounded-lg bg-purple-100 text-purple-700">
                Max: <span className="font-semibold">{latencyStats.maxTotal || 0}ms</span>
              </div>
              {latencyLog.length > 0 && (
                <button
                  onClick={clearLatencyLog}
                  className="px-3 py-2 rounded-lg bg-red-50 text-red-600 font-semibold hover:bg-red-100 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {latencyLog.length > 0 && (
            <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-100 text-center">
                <p className="text-[10px] uppercase text-indigo-700 tracking-wide">Avg VAD / STT (Deepgram)</p>
                <p className="text-xl font-bold text-indigo-900">{latencyStats.avgStt || 0}ms</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-50 border border-purple-100 text-center">
                <p className="text-[10px] uppercase text-purple-700 tracking-wide">Avg KB Lookup</p>
                <p className="text-xl font-bold text-purple-900">{latencyStats.avgKb || 0}ms</p>
              </div>
              <div className="p-3 rounded-lg bg-rose-50 border border-rose-100 text-center">
                <p className="text-[10px] uppercase text-rose-700 tracking-wide">Avg GPT Response</p>
                <p className="text-xl font-bold text-rose-900">{latencyStats.avgGpt || 0}ms</p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100 text-center">
                <p className="text-[10px] uppercase text-emerald-700 tracking-wide">Avg ElevenLabs TTS</p>
                <p className="text-xl font-bold text-emerald-900">{latencyStats.avgTts || 0}ms</p>
              </div>
            </div>
          )}

          <div className="text-xs text-gray-500 mb-4 bg-gray-50 border border-gray-100 rounded-lg p-3 space-y-1">
            <p className="font-semibold text-gray-700">How latency is calculated:</p>
            <p><span className="font-semibold text-blue-600">VAD / STT (Deepgram)</span> — Time from speech start to Deepgram delivering the final transcript (includes voice activity detection).</p>
            <p><span className="font-semibold text-purple-600">KB Lookup</span> — Time to fetch relevant knowledge base chunks for grounding.</p>
            <p><span className="font-semibold text-rose-600">GPT</span> — OpenAI completion duration to craft the response.</p>
            <p><span className="font-semibold text-emerald-600">TTS</span> — ElevenLabs streaming spin‑up for audio playback.</p>
            <p>Total latency is the sum of these stages per exchange.</p>
          </div>

          {latencyLog.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <div className="text-4xl mb-2">📡</div>
              <p className="text-sm">Start a conversation to populate detailed latency analytics.</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[42rem] overflow-y-auto pr-1">
              {latencyLog.map((entry) => {
                const totalLatency =
                  entry.total || entry.stt + entry.kb + entry.gpt + entry.tts || 0;
                const sttPct = totalLatency ? (entry.stt / totalLatency) * 100 : 0;
                const kbPct = totalLatency ? (entry.kb / totalLatency) * 100 : 0;
                const gptPct = totalLatency ? (entry.gpt / totalLatency) * 100 : 0;
                const ttsPct = totalLatency ? (entry.tts / totalLatency) * 100 : 0;

                return (
                  <div key={entry.id} className="border border-gray-200 rounded-2xl overflow-hidden">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                      <div>
                        <p className="text-sm font-semibold">Exchange #{entry.id}</p>
                        <p className="text-xs opacity-80">{formatTime(entry.timestamp)}</p>
                      </div>
                      <div className="text-sm font-semibold bg-white/20 px-3 py-1 rounded-full">
                        ⚡ {totalLatency}ms total
                      </div>
                    </div>

                    <div className="p-4 space-y-3">
                      <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                        <div className="flex justify-between text-xs font-semibold text-blue-700 mb-1 uppercase">
                          <span>🎤 User Input</span>
                          <span>
                            {entry.userChars || 0} chars • {entry.userWords || 0} words
                          </span>
                        </div>
                        <p className="text-sm text-gray-800">
                          {entry.userText || <span className="italic text-gray-500">No transcript</span>}
                        </p>
                      </div>

                      <div className="bg-purple-50 border border-purple-100 rounded-xl p-3">
                        <div className="flex justify-between text-xs font-semibold text-purple-700 mb-1 uppercase">
                          <span>🤖 AI Response</span>
                          <span>
                            {entry.chars || 0} chars • {entry.words || 0} words
                          </span>
                        </div>
                        <p className="text-sm text-gray-800">
                          {entry.assistantText || (
                            <span className="italic text-gray-500">No response captured</span>
                          )}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                        <div className="border rounded-lg p-2">
                          <p className="text-[10px] font-semibold uppercase text-gray-500">STT</p>
                          <p className="text-lg font-bold text-blue-600">{entry.stt || 0}ms</p>
                        </div>
                        <div className="border rounded-lg p-2">
                          <p className="text-[10px] font-semibold uppercase text-gray-500">KB Lookup</p>
                          <p className="text-lg font-bold text-purple-600">{entry.kb || 0}ms</p>
                        </div>
                        <div className="border rounded-lg p-2">
                          <p className="text-[10px] font-semibold uppercase text-gray-500">GPT</p>
                          <p className="text-lg font-bold text-rose-600">{entry.gpt || 0}ms</p>
                        </div>
                        <div className="border rounded-lg p-2">
                          <p className="text-[10px] font-semibold uppercase text-gray-500">TTS</p>
                          <p className="text-lg font-bold text-emerald-600">{entry.tts || 0}ms</p>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs text-gray-500 mb-1">Latency Distribution</p>
                        <div className="flex h-2 rounded-full overflow-hidden bg-gray-200">
                          <div
                            className="bg-blue-500"
                            style={{ width: `${sttPct}%` }}
                            title={`STT ${entry.stt || 0}ms`}
                          />
                          <div
                            className="bg-purple-500"
                            style={{ width: `${kbPct}%` }}
                            title={`KB ${entry.kb || 0}ms`}
                          />
                          <div
                            className="bg-rose-500"
                            style={{ width: `${gptPct}%` }}
                            title={`GPT ${entry.gpt || 0}ms`}
                          />
                          <div
                            className="bg-emerald-500"
                            style={{ width: `${ttsPct}%` }}
                            title={`TTS ${entry.tts || 0}ms`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Performance Tips */}
        <div className="mt-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
          <h3 className="text-lg font-bold text-gray-800 mb-3">⚡ Performance Tips</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="font-semibold text-purple-900 mb-1">🎧 Use Headphones</div>
              <div className="text-gray-700">Prevents echo and improves quality</div>
            </div>
            <div>
              <div className="font-semibold text-purple-900 mb-1">🤫 Quiet Space</div>
              <div className="text-gray-700">Better speech recognition</div>
            </div>
            <div>
              <div className="font-semibold text-purple-900 mb-1">⏸️ Pause Briefly</div>
              <div className="text-gray-700">1-2 second pause triggers response</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

